import { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { TrendingDown, Send, Eye, MousePointerClick, RefreshCw } from 'lucide-react';

/**
 * Admin-only insight strip showing the involuntary-churn recovery funnel
 * for the current calendar month. Powered by /api/admin/winback-analytics
 * which counts:
 *   • users with billing_lapsed_at in the current month (lapsed)
 *   • users with billing_lapsed_email_sent_at  (re-engagement emails sent)
 *   • email_sends rows where type='lapsed_reengagement' & opened_at!=null
 *   • users with billing_restored_at           (card reactivations)
 *
 * Strictly additive — failures load silently so this tile can never
 * break the User Access Management page.
 */
export default function WinbackAnalyticsTile({ token, API }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/winback-analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setStats(data);
      } catch {
        /* silent — tile is optional */
      }
    })();
    return () => { cancelled = true; };
  }, [API, token]);

  if (!stats) return null;

  const cells = [
    { label: 'Lapsed this month', value: stats.lapsed_this_month, icon: TrendingDown, accent: 'text-rose-600' },
    { label: 'Re-engagement emails', value: stats.reengagement_emails_sent, icon: Send, accent: 'text-amber-700' },
    { label: `Opened (${stats.open_rate_pct}%)`, value: stats.emails_opened, icon: Eye, accent: 'text-blue-700' },
    { label: `Clicked (${stats.click_rate_pct ?? 0}%)`, value: stats.emails_clicked ?? 0, icon: MousePointerClick, accent: 'text-indigo-700' },
    { label: `Reactivated (${stats.recovery_rate_pct}%)`, value: stats.card_reactivations, icon: RefreshCw, accent: 'text-emerald-700' },
  ];

  return (
    <Card data-testid="winback-analytics-tile">
      <CardContent className="py-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {cells.map(({ label, value, icon: Icon, accent }) => (
            <div key={label} className="flex items-center gap-3" data-testid={`winback-cell-${label.split(' ')[0].toLowerCase()}`}>
              <Icon className={`w-5 h-5 ${accent}`} />
              <div>
                <div className="text-2xl font-semibold leading-none">{value}</div>
                <div className="text-xs text-muted-foreground mt-1">{label}</div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
